import React, { useState } from 'react';
import {
  AlertCircle,
  Check,
  CreditCard,
  Loader2,
  Lock,
  Plus,
  Shield,
  Star,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { BackButton } from '../../components/ui/BackButton';
import { CardBrandMark } from '../../components/shopper/CardBrandMark';
import { AddPaymentMethodSheet } from '../../components/shopper/AddPaymentMethodSheet';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import {
  formatCardBrand,
  formatExpiry,
  formatWallet,
  isCardExpired,
} from '../../types/payment';
import type { SavedPaymentMethod } from '../../types/payment';

function CardRow({
  method,
  busy,
  onMakeDefault,
  onRemove,
}: {
  method: SavedPaymentMethod;
  busy: boolean;
  onMakeDefault: () => void;
  onRemove: () => void;
}) {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const expired = isCardExpired(method.expMonth, method.expYear);
  const wallet = formatWallet(method.wallet);
  const expiry = formatExpiry(method.expMonth, method.expYear);

  return (
    <div className="bg-white rounded-2xl shadow-soft p-4">
      <div className="flex items-center gap-3">
        <CardBrandMark brand={method.brand} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-heading text-sm">
              {formatCardBrand(method.brand)} &bull;&bull;&bull;&bull; {method.last4}
            </p>
            {method.isDefault && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide bg-purple-50 text-primary px-2 py-0.5 rounded-full">
                <Check className="h-3 w-3" />
                Default
              </span>
            )}
          </div>
          <p className={`text-xs ${expired ? 'text-error' : 'text-body'}`}>
            {wallet ? `${wallet} \u2022 ` : ''}
            {expiry ? (expired ? `Expired ${expiry}` : `Expires ${expiry}`) : 'Card on file'}
          </p>
        </div>

        {busy && <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />}
      </div>

      {confirmingRemove ? (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-body mb-2.5">
            Remove this card? You can add it again at any time.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setConfirmingRemove(false);
                onRemove();
              }}
              disabled={busy}
            >
              Remove card
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmingRemove(false)}>
              Keep
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
          {!method.isDefault && (
            <Button size="sm" variant="ghost" onClick={onMakeDefault} disabled={busy}>
              <Star className="h-3.5 w-3.5 mr-1.5" />
              Set as default
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmingRemove(true)}
            disabled={busy}
            className="text-error hover:bg-red-50 ml-auto"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}

export function PaymentMethodsPage() {
  const { methods, loading, pendingId, error, refresh, remove, makeDefault } =
    usePaymentMethods();
  const [sheetOpen, setSheetOpen] = useState(false);

  const hasCards = methods.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <BackButton fallback="/settings" />
            <h1 className="ml-4 text-xl font-serif text-heading">Payment Methods</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-24">
        {error && (
          <div className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5">
            <AlertCircle className="h-4 w-4 text-error mt-0.5 flex-shrink-0" />
            <p className="text-xs text-error">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-soft p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-12 rounded-md bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-40 rounded bg-gray-200" />
                    <div className="h-3 w-24 rounded bg-gray-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : hasCards ? (
          <div className="space-y-3">
            {methods.map((method) => (
              <CardRow
                key={method.id}
                method={method}
                busy={pendingId === method.id}
                onMakeDefault={() => void makeDefault(method.id).catch(() => {})}
                onRemove={() => void remove(method.id).catch(() => {})}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-5">
              <CreditCard className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-xl font-serif text-heading mb-2">No cards saved yet</h2>
            <p className="text-sm text-body max-w-sm mx-auto">
              Add a card now and checkout becomes a single tap.
            </p>
          </div>
        )}

        <Button
          size="lg"
          fullWidth
          variant={hasCards ? 'outline' : 'default'}
          onClick={() => setSheetOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add payment method
        </Button>

        <div className="space-y-2 pt-2">
          <div className="flex items-center gap-2 text-xs text-body">
            <Shield className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
            <span>Card details are encrypted and stored by Stripe, never by FASH.</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-body">
            <Lock className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
            <span>PCI compliant. We never see your full card number.</span>
          </div>
        </div>

        <p className="text-xs text-center text-body">
          Some boutiques process payments on their own Stripe account. At those stores you
          will be asked for a card at checkout.
        </p>
      </main>

      <AddPaymentMethodSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onAdded={refresh}
      />
    </div>
  );
}
